'use client';

import { AlertCircle, CheckCircle2, Coins, Loader2, MinusCircle } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { BulkCurrencyPicker } from './bulk-currency-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import currencies from '@/data/currencies.json';
import { useBulkUpdateStoreCurrencies, type Store } from '@/lib/api/stores';
import type { BulkCurrencyUpdateResult } from '@/app/stores/utils/bulk-update-currencies';

type BulkUpdateCurrenciesModalProps = {
  stores: Store[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

type ModalStep = 'form' | 'result';

const availableCurrencies = Object.keys(currencies);

const getFailureReason = (reason: string, t: (key: string) => string) => {
  if (reason === 'currency_update_in_progress') return t('table.currenciesDialog.failureInProgress');
  if (reason === 'inngest_unavailable') return t('table.currenciesDialog.failureInngest');
  if (reason === 'unknown_error') return t('table.currenciesDialog.failureUnknown');
  return reason;
};

const notifyBulkCurrencyResult = (
  result: BulkCurrencyUpdateResult,
  t: (key: string, options?: Record<string, unknown>) => string,
  toast: ReturnType<typeof useToast>['toast'],
) => {
  if (result.failed.length > 0 && result.updated === 0) {
    toast({
      title: t('table.currenciesDialog.allFailedTitle'),
      description: t('table.currenciesDialog.allFailedDescription', { count: result.failed.length }),
      variant: 'destructive',
    });
    return;
  }
  if (result.failed.length > 0) {
    toast({
      title: t('table.currenciesDialog.partialFailureTitle'),
      description: t('table.currenciesDialog.partialFailureDescription', {
        updated: result.updated,
        skipped: result.skipped,
        failed: result.failed.length,
      }),
      variant: 'destructive',
    });
    return;
  }
  if (result.updated === 0 && result.skipped > 0) {
    toast({ title: t('table.currenciesDialog.allSkipped', { count: result.skipped }) });
    return;
  }
  toast({
    title: t('table.currenciesDialog.successTitle'),
    description: t('table.currenciesDialog.success', { updated: result.updated, skipped: result.skipped }),
  });
};

type BulkCurrencyResultPanelProps = {
  result: BulkCurrencyUpdateResult;
  t: (key: string, options?: Record<string, unknown>) => string;
};

const BulkCurrencyResultPanel = ({ result, t }: BulkCurrencyResultPanelProps) => (
  <div className="space-y-3 px-6 py-4">
    {result.updated > 0 ? (
      <div className="flex items-start gap-2 text-sm text-green-600">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{t('table.currenciesDialog.resultUpdated', { count: result.updated })}</span>
      </div>
    ) : null}
    {result.skipped > 0 ? (
      <div className="text-muted-foreground flex items-start gap-2 text-sm">
        <MinusCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{t('table.currenciesDialog.resultSkipped', { count: result.skipped })}</span>
      </div>
    ) : null}
    {result.failed.length > 0 ? (
      <div className="space-y-2">
        <div className="text-destructive flex items-start gap-2 text-sm font-medium">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('table.currenciesDialog.resultFailed', { count: result.failed.length })}</span>
        </div>
        <ul className="bg-muted/50 max-h-36 space-y-1.5 overflow-y-auto rounded-md border p-3 text-xs">
          {result.failed.map((item) => (
            <li key={item.storeId}>
              <span className="font-medium">{item.storeName}</span>
              <span className="text-muted-foreground"> — {getFailureReason(item.reason, t)}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : null}
  </div>
);

export const BulkUpdateCurrenciesModal = ({
  stores,
  open,
  onClose,
  onSuccess,
}: BulkUpdateCurrenciesModalProps) => {
  const { t } = useTranslation('stores');
  const { t: tCommon } = useTranslation('common');
  const { toast } = useToast();
  const bulkUpdateMutation = useBulkUpdateStoreCurrencies();
  const [selectedCurrencies, setSelectedCurrencies] = React.useState<string[]>([]);
  const [step, setStep] = React.useState<ModalStep>('form');
  const [result, setResult] = React.useState<BulkCurrencyUpdateResult | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setSelectedCurrencies([]);
    setStep('form');
    setResult(null);
  }, [open]);

  const handleApply = async () => {
    if (selectedCurrencies.length === 0) return;
    try {
      const bulkResult = await bulkUpdateMutation.mutateAsync({
        stores,
        currenciesToAdd: selectedCurrencies,
      });
      setResult(bulkResult);
      setStep('result');
      notifyBulkCurrencyResult(bulkResult, t, toast);
      if (bulkResult.updated > 0) onSuccess?.();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('table.currenciesDialog.error');
      toast({ title: t('table.currenciesDialog.error'), description: message, variant: 'destructive' });
    }
  };

  const handleClose = () => {
    setStep('form');
    setResult(null);
    onClose();
  };

  const isPending = bulkUpdateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && !isPending && handleClose()}>
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
        <DialogHeader className="space-y-1 border-b px-6 py-5 text-left">
          <div className="flex items-center gap-2">
            <div className="bg-primary/10 text-primary flex h-8 w-8 items-center justify-center rounded-md">
              <Coins className="h-4 w-4" />
            </div>
            <DialogTitle>
              {step === 'result' ? t('table.currenciesDialog.resultTitle') : t('table.currenciesDialog.title')}
            </DialogTitle>
          </div>
          <DialogDescription>
            {step === 'result'
              ? t('table.currenciesDialog.resultDescription')
              : t('table.currenciesDialog.description', { count: stores.length })}
          </DialogDescription>
        </DialogHeader>

        {step === 'form' ? (
          <div className="space-y-2 px-6 py-4">
            <Label className="text-sm font-medium">{t('table.currenciesDialog.currenciesLabel')}</Label>
            <p className="text-muted-foreground text-xs">{t('table.currenciesDialog.currenciesHint')}</p>
            <BulkCurrencyPicker
              key={open ? 'currency-picker' : 'currency-picker-closed'}
              currencies={availableCurrencies}
              selected={selectedCurrencies}
              onChange={setSelectedCurrencies}
              searchPlaceholder={t('form.searchCurrency')}
              disabled={isPending}
            />
            <p className="text-muted-foreground text-xs">{t('table.currenciesDialog.asyncHint')}</p>
          </div>
        ) : (
          result && <BulkCurrencyResultPanel result={result} t={t} />
        )}

        <DialogFooter className="border-t px-6 py-4">
          {step === 'form' ? (
            <>
              <Button variant="outline" onClick={handleClose} disabled={isPending}>
                {tCommon('cancel')}
              </Button>
              <Button
                onClick={() => void handleApply()}
                disabled={isPending || selectedCurrencies.length === 0 || stores.length === 0}
              >
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isPending
                  ? t('table.currenciesDialog.applying', { count: stores.length })
                  : t('table.currenciesDialog.confirm')}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>{t('table.currenciesDialog.done')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
